/* eslint-disable react/no-unstable-nested-components */
import { Box, ListItemIcon, MenuItem, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import {
  GRID_TREE_DATA_GROUPING_FIELD,
  GridCellParams,
  GridColDef,
  GridEventListener,
  GridRowParams,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { Dialogs } from '../../constants/Dialogs';
import { openDialog } from '../../ducks/dialogSlice';
import { setMemberDialog } from '../../ducks/group';
import { GroupMember } from '../../types/GroupMember';
import { sortRows } from '../../utils/sortUtils';
import { I18nKeys } from '../../constants/I18nKeys';
import { StatsDashboard } from '../StatsDashboard';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { MuiDataGrid } from '../MUIDataGrid';
import { SortProperty } from '../../types/SortProperty';
import { SortableProperty } from '../../types/SortableProperty';
import { MUIDataGrid, MUIDataGridColumn, usersColumns } from '../../constants/MUIDataGrid';
import { getDefaultColumnDefinition } from '../../utils/MUIDataGridUtils';
import { resetClientIds } from '../../ducks/viewerSlice';
import { changeGroup } from '../../ducks/currentUserSlice';
import { fetchGroups } from '../../ducks/groups';
import { setSecondaryActions } from '../../ducks/secondaryActions';
import { IdeaRoomOnlyIndicator } from '../IdeaRoomOnlyIndicator';
import { isIdeaRoomGroup, isIdeaRoomUser as isIdeaRoomUserFunc } from '../../utils/userUtils';
import { unknownUser } from '../../types/User';
import { PageHeader } from '../library/PageHeader';
import { TableSortMenu } from '../TableSortMenu';
import { UsersCards } from './UsersCards';
import { unknownGroup } from '../../constants/Group';
import { titleCaseToKebabCase } from '../../utils/stringUtils';

const useStyles = makeStyles(() => ({
  groupNameCell: {
    cursor: 'pointer',
  },
}));

interface Props {
  header?: string;
  loading: boolean;
  showToolbar?: boolean;
  members: (GroupMember & { groupName?: string })[];
  statsAndStatus?: boolean;
  table: MUIDataGrid.AllUsers | MUIDataGrid.Users;
}

export const UsersTable: React.FC<Props> = ({
  header,
  loading,
  showToolbar,
  members,
  statsAndStatus = false,
  table,
}: Props) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const apiRef = useGridApiRef();
  const { t } = useTranslation();

  const mobileView = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { availableDealers: dealers = [] } = useAppSelector((state) => state.currentUser);
  const isIdeaRoomUser = useAppSelector((state) => isIdeaRoomUserFunc(state.currentUser.user || unknownUser));
  const { group: { groupId } = unknownGroup } = useAppSelector((state) => state.currentUser);
  const { group: { configurators = [] } = unknownGroup } = useAppSelector((state) => state.group);

  const [sortState, setSortState] = React.useState<SortProperty[]>([]);
  const sortableProperties: SortableProperty[] = [
    { i18nKey: I18nKeys.TableHeaderName, property: 'name' },
    { i18nKey: I18nKeys.TableHeaderEmail, property: 'email' },
    { i18nKey: I18nKeys.TableHeaderDealers, property: 'dealers' },
    { i18nKey: I18nKeys.TableHeaderPermissions, property: 'permissions' },
    { i18nKey: I18nKeys.TableHeaderStatus, property: 'status' },
  ];

  const stats: { i18nKey: string; value: number }[] = [];
  if (statsAndStatus) {
    const totalMembers = members.length;
    const activeMembers = members.filter((member) =>
      ['CONFIRMED', 'RESET_REQUIRED'].includes(member.status || ''),
    ).length;
    const inactiveMembers = members.filter((member) => member.status === 'FORCE_CHANGE_PASSWORD').length;

    stats.push({ i18nKey: I18nKeys.StatsTotalMembers, value: totalMembers });
    stats.push({ i18nKey: I18nKeys.StatsActiveMembers, value: activeMembers });
    stats.push({ i18nKey: I18nKeys.StatsInactiveMembers, value: inactiveMembers });
  }

  const handleExport = useCallback(() => {
    apiRef?.current?.exportDataAsCsv({ fileName: t(I18nKeys.GroupMemberExportCSVTitle) });
  }, [apiRef, t]);

  useLayoutEffect(() => {
    dispatch(
      setSecondaryActions([
        ...(isIdeaRoomUser
          ? [
              <MenuItem key="export" onClick={handleExport}>
                <ListItemIcon>
                  <IdeaRoomOnlyIndicator />
                </ListItemIcon>
                {t(I18nKeys.ExportButton)}
              </MenuItem>,
            ]
          : []),
      ]),
    );
  }, [isIdeaRoomUser, handleExport, t, dispatch]);

  const columns: GridColDef[] = useMemo(() => {
    const baseColumns = usersColumns
      .filter((column) => {
        if (
          [
            MUIDataGridColumn.Configurators,
            MUIDataGridColumn.Dealers,
            MUIDataGridColumn.Status,
            MUIDataGridColumn.Actions,
          ].includes(column)
        )
          return table !== MUIDataGrid.AllUsers;

        if (column === MUIDataGridColumn.Configurators && configurators.length < 2) return false;
        if (column === MUIDataGridColumn.Dealers && dealers.length < 1) return false;
        if (column === MUIDataGridColumn.Status) return statsAndStatus;

        return true;
      })
      .map((column: MUIDataGridColumn) =>
        getDefaultColumnDefinition(
          {
            table,
            dealers,
            configurators,
            showGroupMemberMenu: true,
            editable: false,
          },
          column,
          classes,
          dispatch,
          t,
        ),
      );

    return baseColumns;
  }, [table, dealers, t, statsAndStatus, configurators, classes, dispatch]);

  const handleSortChanged = (sortProperties: SortProperty[]): void => {
    sortRows(members, sortProperties);
    setSortState(sortProperties);
  };

  const onRowClick = React.useCallback<GridEventListener<'rowClick'>>(
    (params: GridRowParams<any>) => {
      if (table !== MUIDataGrid.Users) return;
      const { row: member } = params;
      dispatch(setMemberDialog(member));
      dispatch(openDialog({ dialog: Dialogs.User }));
    },
    [table, dispatch],
  );

  const onCellClick = React.useCallback<GridEventListener<'cellClick'>>(
    (params: GridCellParams) => {
      const { row: { groupId: rowGroupId = '' } = {}, field } = params;
      if (!rowGroupId || field !== [MUIDataGrid.AllUsers, MUIDataGridColumn.Name].join('-')) return;
      dispatch(resetClientIds());
      dispatch(changeGroup(rowGroupId));
      dispatch(fetchGroups());
    },
    [dispatch],
  );

  return (
    <Box height="100%" width="100%" display="flex" flexDirection="column">
      {!mobileView && (
        <MuiDataGrid
          header={header}
          loading={loading}
          showToolbar={showToolbar}
          rows={members}
          columns={columns}
          apiRef={apiRef}
          getRowId={({ path = [], username }) => path.join(':') || username}
          onCellClick={onCellClick}
          onRowClick={onRowClick}
          treeData={table === MUIDataGrid.AllUsers}
          getTreeDataPath={({ path, username }) => path || [username]}
          slots={
            statsAndStatus
              ? {
                  toolbar: () => <StatsDashboard stats={stats} />,
                }
              : undefined
          }
          groupingColDef={getDefaultColumnDefinition(
            {
              table,
              dealers,
              configurators,
              showGroupMemberMenu: true,
              editable: false,
            },
            GRID_TREE_DATA_GROUPING_FIELD,
            classes,
            dispatch,
            t,
          )}
        />
      )}
      {mobileView && statsAndStatus && <StatsDashboard stats={stats} />}
      {mobileView && !isIdeaRoomGroup(groupId) && (
        <PageHeader
          id={`${titleCaseToKebabCase(header)}-header`}
          header={header}
          count={members.length}
          loading={loading}
          buttons={[
            <TableSortMenu
              sortProperties={sortState}
              sortableProperties={sortableProperties}
              onSort={handleSortChanged}
            />,
          ]}
        />
      )}
      {mobileView && <UsersCards filteredUsers={members} />}
    </Box>
  );
};
